import React, { useEffect, useState } from 'react';


import { makeStyles } from 'tss-react/mui';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ChangeRequestForm,
  Grid,
  Progress,
  SectionHeader,
  Typography,
  ChangeRequestDialog,
} from 'web-library';
import { useForm } from 'react-hook-form';
import { AUTHORITY_NAMES, FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { useTranslation } from 'react-i18next';
import { FunctionModule } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { joiResolver } from '@hookform/resolvers/joi';
import { MutationStatus } from 'react-query';
import { REQUEST_TYPES } from 'web-library/models/Request';
import TransactionAuditLogUpdatedDetails from 'web-library/components/TransactionAuditLogUpdatedDetails';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import { usePostSearchApprovers } from '../../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import { mapApproversToDropdown } from '../../../pages/userGroupAccessConfiguration/CreateEditViewUserGroup';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';
import { useGetRequestFileConfigs } from '../../../api-refactored/user-group-access-configuration/useGetRequestFileConfigs';
import schemaSystemSettings from '../schema/schemaSystemSettings';
import TransactionLogModal from '../../../components/TransactionLogModal';
import { useAdminConfiguration } from '../logic/useAdminConfiguration';
import AdminConfigurationItems from './AdminConfigurationItems';

const useStyles = makeStyles()((theme) =>
({
  upperContainer: {
    marginBottom: theme.spacing(6),
  },
  lowerContainer: {
    marginTop: theme.spacing(-2),
  },
  updateButtonContainer: {
    marginTop: theme.spacing(4),
  },
  updatedTransactionContainer: {
    spacing: 0,
    flexDirection: 'row',
    justify: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  viewTransactionLog: {
    marginTop: theme.spacing(6),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      paddingLeft: 0,
    },
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  errorButtonContainer: {
    marginBottom: theme.spacing(7),
  },
}));

const AdminConfiguration = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [files, setFiles] = useState<UploadedFile[]>([]);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [openTransactionLogModal, setOpenTransactionLogModal] = useState<boolean>(false);

  const [openConfirmChangesModal, setOpenConfirmChangesModal] = useState<boolean>(false);

  const { data: authorities } = useGetUserAuthorities();

  const hasEditPermission = authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_SYSTEM_SETTINGS_EDIT);

  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(schemaSystemSettings()),
  });

  const { handleSubmit } = formHook;

  const [configUpdateSuccess, setConfigUpdateSuccess] = useState<boolean>(false);

  const { result, handleConfirmChangesModal } = useAdminConfiguration({
    formHook,
    files,
    setFiles,
    setUploadFileErrorMessage,
    setApiButtonStatus,
    setErrorMessage,
    setConfigUpdateSuccess,
    setOpenConfirmChangesModal,
  });

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
    isLoading: postSearchApproversIsLoading,
  } = usePostSearchApprovers({});

  const { data: requestFilesConfigData } = useGetRequestFileConfigs({});

  useEffect(() => {
    mutatePostSearchApprovers({
      functionName: FUNCTION_NAMES.SYSTEM_SETTINGS,
      functionModule: FunctionModule.ADMIN,
    });
  }, [mutatePostSearchApprovers]);

  if (result.data === undefined || postSearchApproversIsLoading || result.isLoading) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const handleOpenTransactionLogModal = () => setOpenTransactionLogModal(true);

  const handleOpenConfirmChangesModal = () => setOpenConfirmChangesModal(true);

  return (
    <form onSubmit={handleSubmit(handleOpenConfirmChangesModal)}>
      <ContentTemplate isDividerBottom isContinuable>
        <Box className={classes.upperContainer}>
          <SectionHeader title={t('admin->>AdminConfiguration.WebApp Settings')} />
          <AdminConfigurationItems formHook={formHook} resultData={result.data} />
        </Box>
      </ContentTemplate>
      <ContentTemplate continuous type="full">
        <Box className={classes.lowerContainer}>
          <ChangeRequestForm
            formHook={formHook}
            approverDropdowns={mapApproversToDropdown(postSearchApproversData)}
          />
          {requestFilesConfigData && (
            <Grid container spacing={0}>
              <Grid md={6} sm={12} xs={12}>
                <RequestFileDropZone
                  fileConfig={requestFilesConfigData}
                  files={files}
                  setFiles={setFiles}
                  isLoading={apiButtonStatus === 'loading'}
                />
              </Grid>
            </Grid>
          )}
          <TransactionAuditLogUpdatedDetails
            updatedAt={result.data.lastUpdatedOn.value as string}
            updatedBy={result.data.lastUpdatedBy.value as string}
            handleOpenLogModal={handleOpenTransactionLogModal}
            label={t('admin->>UserGroups.ViewTransactionLog')}
          />
          <Box className={classes.errorButtonContainer}>
            {errorMessage && (
              <Typography className={classes.errorText} variant="body2">
                {errorMessage}
              </Typography>
            )}
            {uploadFileErrorMessage && (
              <Typography className={classes.errorText} variant="body2">
                {uploadFileErrorMessage}
              </Typography>
            )}
            {hasEditPermission && (
              <Box className={classes.updateButtonContainer}>
                <ApiButton status={apiButtonStatus} type="submit">
                  {t('admin->>AdminConfiguration.ConfirmChanges')}
                </ApiButton>
                {configUpdateSuccess && (
                  <Typography>
                    {t('admin->>AdminConfiguration.RequestSubmittedSuccessfully')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <TransactionLogModal
            modalState={openTransactionLogModal}
            setOpenModalState={setOpenTransactionLogModal}
            requestType={REQUEST_TYPES.SYSTEM_SETTINGS}
            transactionLogUuid={result.data.transactionLogUuid.value as string}
          />
          <ChangeRequestDialog
            open={openConfirmChangesModal}
            onClose={() => setOpenConfirmChangesModal(false)}
            onConfirm={handleConfirmChangesModal}
          />
        </Box>
      </ContentTemplate>
    </form>
  );
};

export default AdminConfiguration;
